import { MATERIEL_PATHS, stockAt } from '../app/materiel-sections.js';

/** Demandes sur des données personnelles (Mon entreprise › Données personnelles). */
export const GDPR_REQUESTS_PATH = '/entreprise/donnees-personnelles';

export interface AppNotification {
  event: string;
  body?: string | null;
  ref_kind?: string | null;
  ref_id?: string | null;
}

function bodyData(body: string | null | undefined): Record<string, unknown> {
  if (!body) return {};
  try {
    const parsed: unknown = JSON.parse(body);
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Destination métier d'une notification. Les références explicites sont
 * prioritaires ; les données historiques du body gardent un repli compatible. */
export function notificationTarget(notification: AppNotification): string | null {
  const data = bodyData(notification.body);
  const refId = notification.ref_id ?? undefined;
  if (notification.ref_kind === 'schedule_event' && refId) {
    // Décision rendue : le demandeur ouvre son créneau, pas la file des validations
    // (où il n'apparaît plus).
    if (notification.event === 'absence_decidee') return `/planning/temps-bloque/${encodeURIComponent(refId)}`;
    return `/equipe/decisions?id=${encodeURIComponent(refId)}`;
  }
  if (notification.ref_kind === 'intervention' && refId) return `/interventions/${refId}`;
  if (notification.ref_kind === 'task' && refId) return `/taches?id=${encodeURIComponent(refId)}`;
  if (notification.ref_kind === 'invoice' && refId) return `/factures/${refId}`;
  if (notification.ref_kind === 'stock_location' && refId) return stockAt(refId);
  if (notification.ref_kind === 'emergency_alert' && refId) return `/urgences/${refId}`;
  // Astreinte planifiée (19/10/2026) : la liste des astreintes, la période mise en avant.
  if (notification.ref_kind === 'oncall' && refId) return `/equipe/astreintes?id=${encodeURIComponent(refId)}`;
  if (notification.ref_kind === 'support_request') {
    return refId ? `/aide/demandes/${encodeURIComponent(refId)}` : '/aide';
  }
  // Document de l'entreprise à lire (15/10/2026) : sa page de lecture.
  if (notification.ref_kind === 'company_document' && refId) {
    return `/entreprise/documents/${encodeURIComponent(refId)}`;
  }
  // Messages (25/10/2026) : la conversation du message ; un réveil groupé (« N messages
  // non lus », sans conversation) ouvre la rubrique, sur les annonces s'il n'annonce
  // qu'elles ; une annonce (et ses relances), sa page.
  if (notification.ref_kind === 'conversation' && refId) return `/messages/${encodeURIComponent(refId)}`;
  if (notification.ref_kind === 'messages') {
    return notification.event === 'annonce_publiee' ? '/messages?onglet=annonces' : '/messages';
  }
  if (notification.ref_kind === 'announcement' && refId) return `/messages/annonces/${encodeURIComponent(refId)}`;
  if (notification.ref_kind === 'inventory' && refId) return `/stock/inventaires/${encodeURIComponent(refId)}`;
  if (notification.ref_kind === 'purchase_order' && refId) {
    return `/fournisseurs/commandes/${encodeURIComponent(refId)}`;
  }
  // Demande sur des données personnelles (RGPD) : sa page de traitement. Les
  // anciens avis ne portaient que `data.gdprRequestId`.
  const gdprRequestId = notification.ref_kind === 'gdpr_request' && refId ? refId : stringValue(data.gdprRequestId);
  if (gdprRequestId) return `${GDPR_REQUESTS_PATH}/${encodeURIComponent(gdprRequestId)}`;

  const eventId = stringValue(data.eventId);
  const interventionId = stringValue(data.interventionId);
  const alertId = stringValue(data.alertId);
  const quoteId = stringValue(data.quoteId);
  const requestId = stringValue(data.requestId);
  const contractId = stringValue(data.contractId);
  const equipmentId = stringValue(data.equipmentId);
  switch (notification.event) {
    case 'absence_a_valider':
      return eventId ? `/equipe/decisions?id=${encodeURIComponent(eventId)}` : '/equipe/decisions';
    case 'absence_decidee':
      // Ancien avis sans référence : la liste de ses demandes (« Mes absences »).
      return eventId ? `/planning/temps-bloque/${encodeURIComponent(eventId)}` : '/profil/absences';
    case 'affectation_intervention':
    case 'changement_intervention':
    case 'visite_replanifiee':
      return interventionId || eventId ? `/interventions/${interventionId ?? eventId}` : '/planning';
    case 'approbation_requise':
      return stringValue(data.poId)
        ? `/fournisseurs/commandes/${encodeURIComponent(stringValue(data.poId)!)}`
        : MATERIEL_PATHS.commandes;
    case 'document_a_lire':
      return '/entreprise/documents';
    case 'demande_client_recue':
      return requestId ? `/interventions/demandes/${encodeURIComponent(requestId)}` : '/interventions?onglet=demandes';
    case 'note_client_recue':
    case 'signature_impossible':
    case 'cloture_refusee':
    case 'suivi_a_planifier':
    case 'rapport_disponible':
      return interventionId ? `/interventions/${encodeURIComponent(interventionId)}` : null;
    // Sans machine désignée : les machines se retrouvent depuis la fiche client.
    case 'fiche_machine_transferee':
    case 'machine_a_surveiller':
      return equipmentId ? `/equipements/${encodeURIComponent(equipmentId)}` : '/clients';
    case 'contrat_echeance':
      return equipmentId ? `/equipements/${encodeURIComponent(equipmentId)}`
        : contractId ? `/contrats/${encodeURIComponent(contractId)}`
          : interventionId ? `/interventions/${encodeURIComponent(interventionId)}` : '/contrats';
    case 'sla_alerte':
      return interventionId ? `/interventions/${encodeURIComponent(interventionId)}` : '/contrats';
    case 'essai_bientot_termine':
    case 'essai_termine':
      return '/entreprise/abonnement';
    case 'demande_information': {
      // CERFA validé sans le SIRET ou l'attestation de l'entreprise : la page qui les règle.
      const missing = Array.isArray(data.missing) ? data.missing : [];
      if (missing.includes('operator_siret')) return '/entreprise/identite';
      if (missing.includes('capacity_certificate')) return '/entreprise/fluides';
      return null;
    }
    case 'stock_bas':
      // Rubrique Matériel (17/10/2026) : les articles sous le seuil sont dans le catalogue.
      return `${MATERIEL_PATHS.articles}?etat=sous-seuil`;
    case 'anomalie_stock':
    case 'reservation_expiree':
      return MATERIEL_PATHS.surveiller;
    case 'rappel_mise_a_jour_stock':
      return MATERIEL_PATHS.stock;
    case 'message_recu': {
      const conversationId = stringValue(data.conversationId);
      return conversationId ? `/messages/${encodeURIComponent(conversationId)}` : '/messages';
    }
    case 'annonce_publiee': {
      const announcementId = stringValue(data.announcementId);
      return announcementId ? `/messages/annonces/${encodeURIComponent(announcementId)}` : '/messages?onglet=annonces';
    }
    case 'conflit_planning':
      return '/planning';
    case 'astreinte_planifiee':
      return '/equipe/astreintes';
    case 'conflit_synchronisation':
      return '/conflits';
    case 'echec_integration':
      return '/entreprise/integrations';
    case 'urgence_critique':
      return alertId ? `/urgences/${encodeURIComponent(alertId)}` : null;
    case 'facture_contrat_a_verifier': {
      // L'avis porte d'ordinaire `ref_kind` = invoice (traité plus haut) ; repli sur ses données.
      const invoiceId = stringValue(data.invoiceId);
      return invoiceId ? `/factures/${encodeURIComponent(invoiceId)}` : '/factures';
    }
    case 'facture_echue':
    case 'facture_disponible':
      return '/factures';
    case 'devis_a_relancer':
    case 'devis_expire':
    case 'devis_disponible':
      return quoteId ? `/devis/${encodeURIComponent(quoteId)}` : '/devis';
    case 'controle_etancheite_echeance':
      return '/fluides?onglet=echeances';
    case 'fichier_bloque':
      // Fichier bloqué par l'antivirus (20/10/2026) : un fichier ne connaît pas la fiche
      // qui le porte (il peut même n'être rattaché à rien encore) ; le titre nomme le
      // fichier, et c'est depuis l'écran d'envoi habituel qu'on le renvoie.
      return null;
    case 'fuite_constatee':
      return equipmentId ? `/equipements/${encodeURIComponent(equipmentId)}` : '/fluides?onglet=echeances';
    case 'controle_etancheite_absent':
      return interventionId ? `/interventions/${encodeURIComponent(interventionId)}` : null;
    case 'etalonnage_detecteur_echeance':
      return '/fluides?onglet=detecteurs';
    case 'attestation_echeance':
      // Le patron arrive sur la fiche du membre, ou sur les réglages fluides pour l'attestation de l'entreprise ;
      // le technicien prévenu pour la sienne n'a pas de page où aller.
      return typeof data.memberId === 'string' && data.memberId ? `/entreprise/membres/${encodeURIComponent(data.memberId)}`
        : data.target === 'capacite' ? '/entreprise/fluides' : null;
    default:
      return null;
  }
}

/**
 * Fichier préparé pour l'utilisateur (export terminé). La notification « Export
 * prêt » ne menait nulle part : le fichier existait mais restait introuvable.
 */
export function notificationFileId(notification: AppNotification): string | null {
  // Les exports déjà notifiés (conservés 12 mois) portent l'ancien événement
  // « rapport_disponible » avec le même fileId : ils gardent leur lien.
  if (notification.event !== 'export_pret' && notification.event !== 'rapport_disponible') return null;
  const fileId = stringValue(bodyData(notification.body).fileId);
  return fileId && /^[0-9a-f-]{36}$/i.test(fileId) ? fileId : null;
}

